<template>
  <div class="scale-group-select">

    <!-- Одна группа - показываем привычный плоский select -->
    <el-select
      v-if="!useModal"
      v-model="localValue"
      :disabled="disabled"
      :size="size"
      :placeholder="placeholder"
      filterable
      class="scale-group-select-flat"
      @change="onSelect"
    >
      <el-option
        v-for="scale in flatScales"
        :key="scale.id"
        :value="scale.id"
        :label="scale.name || emptyNameText"
      ></el-option>
    </el-select>

    <!-- Групп несколько - открываем окно с колонками -->
    <div
      v-else
      class="scale-group-select-trigger"
      :class="{disabled: disabled}"
      @click="open"
    >
      <span v-if="selectedScale" class="scale-group-select-trigger-value">
        {{ selectedScale.name || emptyNameText }}
      </span>
      <span v-else class="scale-group-select-trigger-placeholder">{{ placeholder }}</span>
      <i class="el-icon-arrow-down scale-group-select-trigger-icon"></i>
    </div>

    <el-dialog
      v-if="useModal"
      title="Выбор шкалы"
      :visible.sync="show"
      width="700px"
      modal-append-to-body
      append-to-body
      :close-on-click-modal="true"
      @open="onDialogOpen"
    >

      <el-input
        v-model="search"
        class="scale-group-select-search"
        size="small"
        placeholder="Поиск по названию шкалы"
        prefix-icon="el-icon-search"
        clearable
      ></el-input>

      <div class="scale-group-select-columns">
        <div class="scale-group-select-column scale-group-select-groups">
          <div
            v-for="group in visibleGroups"
            :key="group.group.id"
            class="scale-group-select-group"
            :class="{active: activeGroupId === group.group.id}"
            @click="activeGroupId = group.group.id"
          >
            <span class="scale-group-select-group-name">{{ group.group.name }}</span>
            <span class="scale-group-select-group-count">{{ group.scales.length }}</span>
          </div>

          <div v-if="!visibleGroups.length" class="scale-group-select-empty">
            Ничего не найдено
          </div>
        </div>

        <div class="scale-group-select-column scale-group-select-scales">
          <div
            v-for="scale in activeGroupScales"
            :key="scale.id"
            class="scale-group-select-scale"
            :class="{active: localValue === scale.id}"
            @click="pick(scale)"
          >
            {{ scale.name || emptyNameText }}
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="show = false">Закрыть</el-button>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import requestSender from "@/api/base/requestSender";

let groupsRequest = null;

export function resetScaleGroupsCache() {
  groupsRequest = null;
}

export default {
  name: "scale-group-select",
  components: {},

  props: {
    value: {},
    disabled: {type: Boolean, default: false},
    size: {type: String, default: 'mini'},
    placeholder: {type: String, default: 'Выберите шкалу'},
    emptyNameText: {type: String, default: 'Нет названия'},
    currentScale: {type: Object, default: null},
  },

  computed: {
    useModal() {
      return this.groups.length > 1;
    },

    flatScales() {
      const scales = this.groups.reduce((acc, group) => acc.concat(group.scales), []);

      if (this.currentScale && !scales.find(s => s.id === this.currentScale.id)) {
        return scales.concat([this.currentScale]);
      }
      return scales;
    },

    visibleGroups() {
      const query = this.search.trim().toLowerCase();
      if (!query) {
        return this.groups;
      }

      return this.groups
        .map(group => ({
          group: group.group,
          scales: group.scales.filter(scale => (scale.name || '').toLowerCase().includes(query)),
        }))
        .filter(group => group.scales.length);
    },

    activeGroupScales() {
      const group = this.visibleGroups.find(g => g.group.id === this.activeGroupId);
      return group ? group.scales : [];
    },

    selectedScale() {
      if (!this.localValue) {
        return null;
      }
      for (const group of this.groups) {
        const scale = group.scales.find(s => s.id === this.localValue);
        if (scale) {
          return scale;
        }
      }

      return this.currentScale && this.currentScale.id === this.localValue
        ? this.currentScale
        : null;
    },
  },

  data() {
    return {
      show: false,
      search: '',
      groups: [],
      activeGroupId: null,
      localValue: this.value,
    }
  },

  watch: {
    value(newVal) {
      this.localValue = newVal;
    },

    search() {
      if (!this.visibleGroups.length) {
        return;
      }
      if (!this.visibleGroups.some(g => g.group.id === this.activeGroupId)) {
        this.activeGroupId = this.visibleGroups[0].group.id;
      }
    },
  },

  mounted() {
    this.loadGroups();
  },

  methods: {
    loadGroups() {
      if (!groupsRequest) {
        groupsRequest = requestSender('get', 'scale/grouped-templates-list', {
          sort: 'name',
        })

          .then(data => data.groups.filter(group => group.scales.length))
          .catch(e => {
            groupsRequest = null;
            throw e;
          });
      }

      return groupsRequest
        .then(groups => {
          this.groups = groups;
        })
        .catch(() => {});
    },

    open() {
      if (this.disabled) {
        return;
      }
      this.show = true;
    },

    onDialogOpen() {
      this.search = '';
      const currentGroup = this.groups.find(g => g.scales.some(s => s.id === this.localValue));
      if (currentGroup) {
        this.activeGroupId = currentGroup.group.id;
        return;
      }
      this.activeGroupId = this.groups.length ? this.groups[0].group.id : null;
    },

    pick(scale) {
      this.localValue = scale.id;
      this.show = false;
      this.emit(scale.id);
    },

    onSelect(id) {
      this.emit(id);
    },

    emit(id) {
      this.$emit('input', id);
      this.$emit('change', id);
    },
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/css/project-variables";
@import "@/components/configurableScales/scalePills.scss";

.scale-group-select-flat {
  width: 100%;
}

// Кнопка-триггер повторяет вид el-input, чтобы в форме читалась как обычное поле
.scale-group-select-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #DCDFE6;
  border-radius: $--border-radius-base;
  padding: 0 10px;
  height: 28px;
  line-height: 28px;
  cursor: pointer;
  background-color: #ffffff;
  transition: border-color 0.2s;

  &:hover {
    border-color: #C0C4CC;
  }

  &.disabled {
    background-color: #F5F7FA;
    border-color: #E4E7ED;
    color: #C0C4CC;
    cursor: not-allowed;
  }
}

.scale-group-select-trigger-value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scale-group-select-trigger-placeholder {
  flex: 1;
  color: #C0C4CC;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scale-group-select-trigger-icon {
  color: #C0C4CC;
  flex-shrink: 0;
}

.scale-group-select-search {
  margin-bottom: 15px;
}

.scale-group-select-columns {
  display: flex;
  gap: 20px;
  min-height: 260px;
  max-height: 400px;
}

.scale-group-select-column {
  overflow-y: auto;
}

.scale-group-select-groups {
  width: 240px;
  flex-shrink: 0;
  border-right: 1px solid #E9E9E9;
  padding-right: 20px;
}

.scale-group-select-scales {
  flex: 1;
}

.scale-group-select-group {
  @include scale-pill($scale-pill-border-strong);

  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  &.active {
    @include scale-pill-active;
  }
}

.scale-group-select-group-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scale-group-select-group-count {
  color: #909399;
  font-size: 12px;
  flex-shrink: 0;
}

.scale-group-select-scale {
  @include scale-pill;

  margin-bottom: 8px;

  &.active {
    @include scale-pill-active;
  }
}

.scale-group-select-empty {
  color: #909399;
  text-align: center;
  padding: 40px 0;
}
</style>